import { lazy, FC, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { MasterLayout } from "../../_mcp/layout/MasterLayout";
import { WithChildren } from "../../_mcp/helpers";
import { getCSSVariableValue } from "../../_mcp/assets/ts/_utils";
import TopBarProgress from "react-topbar-progress-indicator";
import UserManagementPage from "../pages/user-management/UserManagementPage";
import { useAuth } from "../modules/auth";

const InternalRoutes = () => {
  const { currentUser } = useAuth();

  // Determine user role and redirect accordingly
  const getUserRole = () => {
    if (!currentUser?.auth?.role) return null;
    return currentUser.auth.role;
  };

  const role = getUserRole();

  // Check if user has INTERNAL role, redirect to auth if not
  if (!role?.startsWith("INTERNAL")) {
    return <Navigate to="/auth" replace />;
  }

  const UserFundsPage = lazy(() => import("../pages/user-funds/UserFundsPage"));
  const RefundsPage = lazy(() => import("../pages/refunds/RefundsPage"));
  const CheckUserFundsPage = lazy(
    () => import("../pages/check-user-funds/UserFundsPage")
  );
  const TransactionHistoryPage = lazy(
    () => import("../pages/transaction-history/TransactionHistoryPage")
  );

  const MerchantManagementPage = lazy(
    () => import("../pages/merchant-management/MerchantManagementPage")
  );

  // Role-based route access
  const canAccessMerchantManagement = role === "INTERNAL_ADMIN";
  const canAccessUserManagement = role === "INTERNAL_ADMIN";
  const canAccessCheckUserFunds = role === "INTERNAL_ADMIN";
  const canAccessUserFunds =
    role === "INTERNAL_MAKER" || role === "INTERNAL_APPROVAL";
  const canAccessRefunds =
    role === "INTERNAL_MAKER" || role === "INTERNAL_APPROVAL";
  const canAccessTransactionHistory =
    role === "INTERNAL_ADMIN" ||
    role === "INTERNAL_MAKER" ||
    role === "INTERNAL_APPROVAL";

  return (
    <Routes>
      <Route element={<MasterLayout />}>
        {/* Internal Pages */}
        <Route
          path="auth/*"
          element={<Navigate to="/internal/transaction-history" />}
        />
        <Route
          path="merchant-management/*"
          element={
            canAccessMerchantManagement ? (
              <SuspensedView>
                <MerchantManagementPage />
              </SuspensedView>
            ) : (
              <Navigate to="/internal/transaction-history" replace />
            )
          }
        />
        <Route
          path="user-management/*"
          element={
            canAccessUserManagement ? (
              <SuspensedView>
                <UserManagementPage />
              </SuspensedView>
            ) : (
              <Navigate to="/internal/transaction-history" replace />
            )
          }
        />

        <Route
          path="user-funds/*"
          element={
            canAccessUserFunds ? (
              <SuspensedView>
                <UserFundsPage />
              </SuspensedView>
            ) : (
              <Navigate to="/internal/transaction-history" replace />
            )
          }
        />
        <Route
          path="refunds/*"
          element={
            canAccessRefunds ? (
              <SuspensedView>
                <RefundsPage />
              </SuspensedView>
            ) : (
              <Navigate to="/internal/transaction-history" replace />
            )
          }
        />
        <Route
          path="check-user-funds/*"
          element={
            canAccessCheckUserFunds ? (
              <SuspensedView>
                <CheckUserFundsPage />
              </SuspensedView>
            ) : (
              <Navigate to="/internal/transaction-history" replace />
            )
          }
        />
        <Route
          path="transaction-history/*"
          element={
            canAccessTransactionHistory ? (
              <SuspensedView>
                <TransactionHistoryPage />
              </SuspensedView>
            ) : (
              <Navigate to="/internal/transaction-history" replace />
            )
          }
        />
        {/* Page Not Found */}
        <Route path="*" element={<Navigate to="/error/404" />} />
      </Route>
    </Routes>
  );
};

const SuspensedView: FC<WithChildren> = ({ children }) => {
  const baseColor = getCSSVariableValue("--kt-primary");
  TopBarProgress.config({
    barColors: {
      "0": baseColor,
    },
    barThickness: 1,
    shadowBlur: 5,
  });
  return <Suspense fallback={<TopBarProgress />}>{children}</Suspense>;
};

export { InternalRoutes };
